// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import React from "react";

import { VizPopulationOverTime } from "~shared-pathways";

import { useCoreStore } from "../CoreStoreProvider";
import OverTimeMetric from "../models/OverTimeMetric";
import PersonLevelMetric from "../models/PersonLevelMetric";
import PopulationProjectionOverTimeMetric from "../models/PopulationProjectionOverTimeMetric";
import SnapshotMetric from "../models/SnapshotMetric";
import SupervisionPopulationSnapshotMetric from "../models/SupervisionPopulationSnapshotMetric";
import withPathwaysMetricHelpers from "../PathwaysMetricHelpers/withPathwaysMetricHelpers";
import VizCountOverTimeWithAvg from "../VizCountOverTimeWithAvg";
import VizLengthOfStay from "../VizLengthOfStay";
import VizPopulationPersonLevel from "../VizPopulationPersonLevel";
import VizPopulationProjectionOverTime from "../VizPopulationProjectionOverTime";
import VizPopulationSnapshot from "../VizPopulationSnapshot";

const HydratedVizPopulationOverTime = withPathwaysMetricHelpers(
  VizPopulationOverTime,
);

type MetricVizMapperProps = {
  metric:
    | OverTimeMetric
    | SnapshotMetric
    | PersonLevelMetric
    | PopulationProjectionOverTimeMetric
    | SupervisionPopulationSnapshotMetric;
};

const MetricVizMapper: React.FC<MetricVizMapperProps> = ({ metric }) => {
  const { filtersStore } = useCoreStore();

  if (metric instanceof OverTimeMetric) {
    switch (metric.id) {
      case "prisonPopulationOverTime":
      case "supervisionPopulationOverTime":
        return (
          <HydratedVizPopulationOverTime
            metric={metric}
            filtersStore={filtersStore}
          />
        );
      default:
        return <VizCountOverTimeWithAvg metric={metric} />;
    }
  }

  if (metric instanceof SnapshotMetric) {
    switch (metric.id) {
      case "supervisionToPrisonPopulationByLengthOfStay":
      case "supervisionToLibertyPopulationByLengthOfStay":
        return <VizLengthOfStay metric={metric} />;
      default:
        // Adding "key" here forces React to unmount and remount the component. Without it, when
        // changing pages, there is a brief flash of data before the loading indicator shows up.
        return <VizPopulationSnapshot metric={metric} key={metric.id} />;
    }
  }

  if (metric instanceof PersonLevelMetric) {
    return <VizPopulationPersonLevel metric={metric} />;
  }

  if (metric instanceof PopulationProjectionOverTimeMetric) {
    return <VizPopulationProjectionOverTime metric={metric} />;
  }

  if (metric instanceof SupervisionPopulationSnapshotMetric) {
    // Old backend metric (currently only supervisionToPrisonPopulationByOfficer).
    // Cast needed because SupervisionPopulationSnapshotMetric doesn't extend the
    // shared SnapshotMetric type. This is safe because SupervisionPopulationSnapshotMetric extends
    // PathwaysMetric which has everything that the viz needs.
    // - This will go away when the metric moves to the new backend.
    // TODO(#3132) Launch count by officer on the new backend
    return <VizPopulationSnapshot metric={metric as any} />;
  }

  // there are no other metric types, so this should only be reached when developing new ones
  throw new Error("unknown metric type");
};

export default MetricVizMapper;
